// PlayFirst Parent — Home (vintage-pastel palette, Feb 2026).
// Palette anchors:
//   Cream         #FFF8E1   — page wash, card surfaces
//   Light Lavender#EAD7D1   — blush/dusty rose accents, encouraging cards
//   Soft Pink     #F5CAC3   — heart accents, collaborator tile, ribbons
//   Pastel Blue   #A3C4BC   — language-map tile, CTA accent
//   Mint Green    #D9EAD3   — where-they-are tile, GLP note
//   Pale Lemon    #FFE3B3   — toys tile, strategy card warm hint
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import {
  Heart, Sparkles, Settings, RefreshCw, Loader2, LogOut, ArrowRight, BookOpen,
  ThumbsUp, ThumbsDown, Check, MessageSquare, Users2, MessageCircle, X, Eye, Plus,
  Share2, Package, Camera,
} from 'lucide-react';
import { PFP } from './styles';
import ActivityFeedbackDialog from './ActivityFeedbackDialog';
import WhatsAppOnboardSplash from './WhatsAppOnboardSplash';
import ParentBloomCard from './ParentBloomCard';
import NotificationsBell from './NotificationsBell';
import ProfileRefreshCard from './ProfileRefreshCard';
import { ConnectTherapistCard } from './CrossJoinCards';
import TherapistLinksCard from './TherapistLinksCard';
import MyChildGoalsCard from './MyChildGoalsCard';
import { GoalBadge } from './ParentAppGoals';
import ExternalTherapistCard, { useHasActiveExternalTherapist } from './ExternalTherapistCard';
import { pickActiveChild, setActiveChildId } from './childSelection';
import { shareParentPlayIdeaPdf } from '@/lib/shareParentPlayIdeaPdf';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DIFF_LABEL = {
  too_easy: 'Too easy',
  just_right: 'Just right',
  too_hard: 'Too hard',
};
const ENG_LABEL = {
  not_interested: 'Not interested',
  curious: 'Curious',
  engaged: 'Engaged',
  loved_it: 'Loved it',
};

// Quick-access tiles — vintage pastel palette (Feb 2026).
// Each tile owns one of the palette's four "warm" anchors so the grid reads
// as a curated, calm composition — never saturated.
const QUICK_TILES = [
  {
    key: 'toys',
    label: 'Toys & interests',
    desc: 'Save a toy or a 30-second moment — get a tailored play idea.',
    icon: Package,
    href: (childId) => `/parent-app/toys?child=${childId}`,
    // Pale Lemon #FFE3B3
    bg: 'from-[#FFF6E0] via-[#FFEFC8] to-[#FFE3B3]',
    iconBg: 'bg-white/80 text-[#B88A3A]',
    testid: 'home-tile-toys',
  },
  {
    key: 'language-map',
    label: 'Language Map',
    desc: "Their strengths, what helps, what's emerging.",
    icon: BookOpen,
    href: () => '/parent-app/language-map',
    // Pastel Blue #A3C4BC
    bg: 'from-[#E2EDEA] via-[#C9DCD6] to-[#A3C4BC]',
    iconBg: 'bg-white/80 text-[#5A7A72]',
    testid: 'home-tile-language-map',
  },
  {
    key: 'where',
    label: 'Where they are',
    desc: "A gentle, never-pathologising snapshot at this age.",
    icon: Eye,
    href: () => '/parent-app/where-they-are',
    // Mint Green #D9EAD3
    bg: 'from-[#EEF5E9] via-[#E4F0DD] to-[#D9EAD3]',
    iconBg: 'bg-white/80 text-[#6B8F5A]',
    testid: 'home-tile-where',
  },
  {
    key: 'collab',
    label: 'Our Collaborators',
    desc: 'Courses, services and SLPs — many with special deals.',
    icon: Users2,
    href: () => '/parent-app/collaborators',
    // Soft Pink #F5CAC3
    bg: 'from-[#FBE7E2] via-[#F8D8D1] to-[#F5CAC3]',
    iconBg: 'bg-white/80 text-[#B26B5E]',
    testid: 'home-tile-collab',
  },
];

export default function ParentAppHome() {
  const navigate = useNavigate();
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
  })();
  const [children, setChildren] = useState([]);
  const [child, setChild] = useState(null);
  const [languageMap, setLanguageMap] = useState(null);
  const [playIdea, setPlayIdea] = useState(null);
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [childSwitching, setChildSwitching] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [sharingPdf, setSharingPdf] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [showWhatsappSplash, setShowWhatsappSplash] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [sharingDashboard, setSharingDashboard] = useState(false);
  const shareCardRef = useRef(null);
  const hasExternalTherapist = useHasActiveExternalTherapist();

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadChildBundle = useCallback(async (c) => {
    setLanguageMap(null);
    setPlayIdea(null);
    setFeedbackList([]);
    try {
      const lm = await axios.get(`${API}/parent-app/children/${c.id}/language-map`, auth());
      setLanguageMap(lm.data);
    } catch (e) {
      if (e.response?.status === 404) {
        const lm = await axios.post(`${API}/parent-app/children/${c.id}/language-map`, {}, auth());
        setLanguageMap(lm.data);
      }
    }
    try {
      const pi = await axios.get(`${API}/parent-app/children/${c.id}/play-idea/today`, auth());
      setPlayIdea(pi.data);
    } catch (e) {
      if (e.response?.status === 404) {
        const pi = await axios.post(`${API}/parent-app/children/${c.id}/play-idea`, {}, auth());
        setPlayIdea(pi.data);
      }
    }
    await refreshFeedback(c.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const existingFeedback = useMemo(() => {
    if (!playIdea?.id) return null;
    return feedbackList.find((f) => f.play_idea_id === playIdea.id) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playIdea?.id, feedbackList]);

  const refreshFeedback = useCallback(async (childId) => {
    try {
      const res = await axios.get(`${API}/parent-app/children/${childId}/feedback`, auth());
      setFeedbackList(res.data || []);
    } catch (e) { /* silent */ }
  }, []);

  const init = useCallback(async () => {
    try {
      const listRes = await axios.get(`${API}/parent-app/children`, auth());
      const list = Array.isArray(listRes.data) ? listRes.data : [];
      if (list.length === 0) {
        navigate('/parent-app/onboarding');
        return;
      }
      setChildren(list);
      const picked = pickActiveChild(list);
      setChild(picked);
      await loadChildBundle(picked);
      try {
        const ws = await axios.get(`${API}/parent-app/whatsapp/status`, auth());
        setWhatsappStatus(ws.data);
        if (
          !ws.data?.verified
          && typeof window !== 'undefined'
          && !localStorage.getItem('playfirst_whatsapp_intro_seen')
        ) {
          setShowWhatsappSplash(true);
        }
      } catch (e) {
        /* silent — feature degrades to banner-only */
      }
    } catch (err) {
      // 401 — token invalid/expired; clear and go to login.
      const status = err?.response?.status;
      if (status === 401) {
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        } catch { /* noop */ }
        navigate('/parent-app/login');
        return;
      }
      // 403 — signed in but server rejected PFP entitlement. This
      // should be impossible after the /auth/me self-heal above, but
      // if it slips through (e.g. server-side twin logic disagreeing
      // with `_require_parent_self`) do NOT dump the parent to login.
      // Route to pricing so she has a clear next step instead of a
      // baffling sign-in loop. Feb 2026 — Jayde.
      if (status === 403) {
        navigate('/parent-app/pricing');
        return;
      }
      toast.error('Failed to load — please refresh');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshFeedback]);

  const switchChild = useCallback(async (newChildId) => {
    if (!newChildId || newChildId === child?.id) return;
    const next = children.find((c) => c.id === newChildId);
    if (!next) return;
    setChildSwitching(true);
    setChild(next);
    setActiveChildId(next.id);
    try {
      await loadChildBundle(next);
    } finally {
      setChildSwitching(false);
    }
  }, [child?.id, children, loadChildBundle]);

  useEffect(() => {
    // Feb 2026 — SELF-HEAL entitlement gate.
    //
    // The naive check below (any of role/flag/caps/sub) fails for Jayde
    // in production: her localStorage user is somehow missing every PFP
    // signal even though her DualRoleTabBar (same source of truth)
    // renders both tabs. Rather than trust stale localStorage, call
    // /auth/me first — the backend RE-COMPUTES capabilities every call
    // (see _compute_user_capabilities()) so a fresh /auth/me is the
    // definitive source of truth. If it says PFP-yes, hydrate and
    // continue. If it says PFP-no, THEN redirect to login/pricing.
    //
    // A user without a token was never signed in — send straight to
    // login without the /auth/me round-trip.
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      navigate('/parent-app/login');
      return;
    }

    const localHasPfp = user && (
      user.role === 'parent_self'
      || user.has_parent_app_access
      || user.capabilities?.pfp
      || (user.parent_subscription_status || '').toLowerCase() === 'active'
    );

    // Fast path — localStorage already proves PFP entitlement. Skip
    // the /auth/me hop for the 99% of users whose data is fresh.
    if (localHasPfp) {
      init();
      return;
    }

    // Slow path — hydrate from server, self-heal localStorage, retry.
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`, auth());
        const fresh = me?.data;
        if (fresh) {
          try { localStorage.setItem('user', JSON.stringify(fresh)); } catch {}
          const serverHasPfp = (
            fresh.role === 'parent_self'
            || fresh.has_parent_app_access
            || fresh.capabilities?.pfp
            || (fresh.parent_subscription_status || '').toLowerCase() === 'active'
          );
          if (serverHasPfp) {
            // Re-render will read the fresh user; kick off init directly.
            init();
            return;
          }
          // Server confirms no PFP entitlement — send to pricing, not
          // login (they're signed in, just not subscribed yet).
          navigate('/parent-app/pricing');
          return;
        }
        // /auth/me returned no data — treat as unauthenticated.
        navigate('/parent-app/login');
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          try {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          } catch { /* noop */ }
          navigate('/parent-app/login');
          return;
        }
        // Transient/network — DON'T dump them to login. Let init run;
        // it has its own error handling and the ErrorBoundary catches
        // any downstream crash.
        init();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const regeneratePlayIdea = async () => {
    if (!child) return;
    setRegenerating(true);
    try {
      const pi = await axios.post(`${API}/parent-app/children/${child.id}/play-idea`, {}, auth());
      setPlayIdea(pi.data);
      toast.success('A fresh idea — shaped by your last feedback');
    } catch (e) {
      toast.error('Could not generate a new idea');
    } finally {
      setRegenerating(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/parent-app');
  };

  const handleSharePdf = async () => {
    if (!playIdea?.id || sharingPdf) return;
    setSharingPdf(true);
    const result = await shareParentPlayIdeaPdf({
      playIdeaId: playIdea.id,
      activityName: playIdea.activity?.activity_name,
      childFirstName: child?.name,
    });
    setSharingPdf(false);
    if (result === 'shared') toast.success('Shared!');
    else if (result === 'downloaded') toast.success('Saved to your device');
    else if (result === 'error') toast.error("Couldn't generate the PDF — please try again");
  };

  // Capture the hero + Play Idea + Explore grid as a PNG so the parent can
  // share their dashboard to socials/WhatsApp without manual screenshotting.
  // Uses html-to-image (already in deps) — Web Share API on mobile, file
  // download fallback on desktop.
  const handleShareDashboard = async () => {
    if (!shareCardRef.current || sharingDashboard) return;
    setSharingDashboard(true);
    try {
      const dataUrl = await toPng(shareCardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#FFF8E1',
        // skip elements with the data-share-exclude attribute (e.g. the
        // share button itself, the WhatsApp banner)
        filter: (node) => !node?.dataset?.shareExclude,
      });
      const blob = await (await fetch(dataUrl)).blob();
      const filename = `playfirst-${(child?.name || 'today').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.png`;
      const file = new File([blob], filename, { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: `${child?.name || 'My child'}'s PlayFirst today`,
            text: `Today's gentle play idea from PlayFirst Parent. 💛`,
          });
          toast.success('Shared!');
        } catch (e) {
          if (e?.name !== 'AbortError') throw e;
        }
      } else {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Saved to your device');
      }
    } catch (e) {
      console.error('share dashboard error', e);
      toast.error("Couldn't capture the dashboard — please try again");
    } finally {
      setSharingDashboard(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[linear-gradient(180deg,#FFF9F2_0%,#FBF4F9_100%)] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#7C6F9C]" />
      </div>
    );
  }

  // Feb 2026 — dual-role parents can reach /parent-app/home with a paid
  // subscription but no PFP children yet (e.g. Jayde on prod). init()
  // routes them to /parent-app/onboarding, but before the redirect
  // settles this component still renders once with children=[] and
  // child=null. Rendering QUICK_TILES then crashed on child.id ->
  // blank white page. Bail with a gentle placeholder in that window.
  if (!user || !child) {
    return (
      <div className="min-h-screen bg-[linear-gradient(180deg,#FFF9F2_0%,#FBF4F9_100%)] flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <Loader2 className="w-6 h-6 animate-spin text-[#7C6F9C] mx-auto" />
          <p className="text-sm text-stone-500">Getting your home ready…</p>
        </div>
      </div>
    );
  }

  const lm = languageMap?.map;
  const act = playIdea?.activity;
  const firstName = user?.name?.split(' ')[0] || 'there';
  const childFirstName = child?.name?.split(' ')[0] || 'your child';

  return (
    <div className="relative min-h-screen overflow-hidden bg-[linear-gradient(180deg,#FFF8E1_0%,#FBE9D6_28%,#EAD7D1_60%,#F5CAC3_100%)]">
      {/* Decorative pastel blobs — vintage palette */}
      <div className="pointer-events-none absolute -top-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#A3C4BC] opacity-30 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -left-32 w-[26rem] h-[26rem] rounded-full bg-[#FFE3B3] opacity-50 blur-3xl" />
      <div className="pointer-events-none absolute top-[55%] right-1/4 w-[22rem] h-[22rem] rounded-full bg-[#D9EAD3] opacity-40 blur-3xl" />
      <div className="pointer-events-none absolute top-[80%] -left-20 w-[20rem] h-[20rem] rounded-full bg-[#F5CAC3] opacity-40 blur-3xl" />

      {/* Header — translucent, sits on the gradient */}
      <header className="relative z-10 px-4 sm:px-8 py-4 max-w-5xl mx-auto flex items-center justify-between gap-3">
        <Link to="/parent-app/home" className="flex items-center gap-2 flex-shrink-0">
          <Heart className="w-6 h-6 text-[#7C6F9C] fill-[#7C6F9C]/20" />
          <span className="font-serif text-xl text-[#3a2f4f] hidden sm:inline">PlayFirst</span>
          <span className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium ml-1 hidden sm:inline">
            Parent
          </span>
        </Link>

        {child && (
          children.length > 1 ? (
            <div className="flex-1 flex items-center justify-center gap-2 max-w-md">
              <Select value={child.id} onValueChange={switchChild}>
                <SelectTrigger
                  className="h-9 rounded-full bg-white/80 border-white/60 backdrop-blur text-sm flex-1 max-w-[220px]"
                  data-testid="parent-app-child-picker"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {children.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`parent-app-child-option-${c.id}`}>
                      {c.name || 'Unnamed'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={() => navigate('/parent-app/onboarding?add=1')}
                className="h-9 w-9 rounded-full bg-white/70 text-[#7C6F9C] hover:bg-white inline-flex items-center justify-center flex-shrink-0 backdrop-blur"
                title="Add another child"
                data-testid="parent-app-add-child-btn"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate('/parent-app/onboarding?add=1')}
              className="text-xs px-3 py-1.5 rounded-full bg-white/70 backdrop-blur text-[#7C6F9C] hover:bg-white inline-flex items-center gap-1"
              data-testid="parent-app-add-child-btn"
              title="Add another child"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add another child</span>
            </button>
          )
        )}

        <div className="flex items-center gap-1 flex-shrink-0">
          <NotificationsBell />
          <Link to="/parent-app/collaborators">
            <button
              className="p-2 rounded-full hover:bg-white/60"
              data-testid="parent-app-collaborators-link"
              title="Our Collaborators"
            >
              <Users2 className="w-5 h-5 text-stone-600" />
            </button>
          </Link>
          <Link to="/parent-app/settings">
            <button className="p-2 rounded-full hover:bg-white/60" data-testid="parent-app-settings-link">
              <Settings className="w-5 h-5 text-stone-600" />
            </button>
          </Link>
          <button
            onClick={logout}
            className="p-2 rounded-full hover:bg-red-50 text-red-500"
            data-testid="parent-app-logout-btn"
            title="Sign out"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-4 pt-4 md:pt-8 pb-32 space-y-6">
        {/* Sep 2026 — Pending / active INBOUND therapist link requests
            (therapist invited THIS parent). Was silently missing on
            the parent side, so a therapist inviting a parent stayed in
            "pending" forever with no way to accept. */}
        <TherapistLinksCard children={children} />

        {/* Feb 2026 — External / non-PlayFirst therapist summary. Reads
            `therapist_lite_links` + current pfp_priority_goals so the
            parent can see who's connected and which goals are steering
            their play ideas. Hidden when there's no active external link.*/}
        <ExternalTherapistCard />

        {/* Cross-join CTA — only when the parent has NO therapist connection
            at all (Clinical OR external). Was previously hidden only on
            Clinical connection, so external-therapist parents kept seeing
            "Connect with therapist" even after their external therapist
            had accepted. */}
        {(() => {
          try {
            const u = JSON.parse(localStorage.getItem('user') || 'null');
            const hasClinical = !!u?.capabilities?.clinical_parent;
            // hasExternalTherapist === null while loading — treat as "hide"
            // so the CTA doesn't flash for a beat then vanish.
            if (!hasClinical && hasExternalTherapist === false) {
              return <ConnectTherapistCard />;
            }
          } catch { /* silent */ }
          return null;
        })()}

        {childSwitching && (
          <div
            className="rounded-2xl border border-white/60 bg-white/70 backdrop-blur px-4 py-2 text-sm text-stone-500 inline-flex items-center gap-2"
            data-testid="parent-app-child-switching"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading {childFirstName}'s today…
          </div>
        )}

        {/* ============ SHAREABLE DASHBOARD CARD ============ */}
        {/* Everything inside shareCardRef is captured by html-to-image when
            the parent taps "Share my dashboard". Elements with
            data-share-exclude are filtered out at capture time. */}
        <div ref={shareCardRef} className="space-y-6 rounded-3xl">
          {/* ============ HERO ============ */}
          <motion.section
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative"
            data-testid="parent-app-hero"
          >
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-3xl bg-gradient-to-br from-[#FFF8E1] via-[#EAD7D1] to-[#F5CAC3] flex items-center justify-center text-[#3a2f4f] font-serif text-3xl shadow-md shadow-[#A3C4BC]/30 ring-1 ring-white/80 flex-shrink-0">
                {(child?.name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#7C6F9C] font-medium mb-1">Hi {firstName} — glad you're here ✨</p>
                <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#3a2f4f] leading-tight">
                  One small step for <span className="italic">{child?.name}</span> today
                </h1>
                <p className="text-sm sm:text-base text-stone-600 mt-2 max-w-2xl leading-relaxed">
                  Connection first. A smile, a gesture, a sound, a script, a word — it all counts. 💛
                </p>
              </div>
            </div>

            {/* Interest pills + compact profile meta + share button */}
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {(() => {
                // Feb 2026 — compute display age from date_of_birth first
                // (self-updating). Fall back to legacy static age_years.
                // Old bug: raw age_years=0.08 was rendered as "0.08 years"
                // for a child entered as "1 month" — never re-computed
                // after DOB was set on Settings.
                const dob = child?.date_of_birth;
                let months = null;
                if (dob) {
                  const d = new Date(dob);
                  if (!isNaN(d.getTime())) {
                    const now = new Date();
                    months = Math.max(
                      0,
                      Math.round(((now - d) / (1000 * 60 * 60 * 24)) / 30.4375),
                    );
                  }
                }
                if (months == null && child?.age_years != null) {
                  months = Math.round(Number(child.age_years) * 12);
                }
                if (months == null || isNaN(months)) return null;
                const y = Math.floor(months / 12);
                const m = months % 12;
                let label;
                if (y === 0 && m === 0) label = 'newborn';
                else if (y === 0) label = `${m} month${m === 1 ? '' : 's'}`;
                else if (m === 0) label = `${y} year${y === 1 ? '' : 's'}`;
                else label = `${y}y ${m}m`;
                return (
                  <span
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white/70 backdrop-blur text-stone-700 border border-white/60"
                    data-testid="child-age-pill"
                  >
                    {label}
                  </span>
                );
              })()}
              {(child?.languages_at_home || []).slice(0, 2).map((l) => (
                <span key={l} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white/70 backdrop-blur text-stone-700 border border-white/60">
                  {l}
                </span>
              ))}
              {(child?.interests || []).slice(0, 4).map((it) => (
                <span key={it} className="text-xs px-3 py-1.5 rounded-full bg-white/70 backdrop-blur text-[#5b4f7a] border border-white/60">
                  {it}
                </span>
              ))}
              <Link
                to="/parent-app/settings"
                className="text-xs px-3 py-1.5 rounded-full bg-[#7C6F9C]/15 hover:bg-[#7C6F9C]/25 text-[#5b4f7a] inline-flex items-center gap-1"
                data-testid="edit-profile-link"
                data-share-exclude="true"
              >
                <Settings className="w-3 h-3" /> Edit profile
              </Link>
              <button
                onClick={handleShareDashboard}
                disabled={sharingDashboard}
                className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-[#A3C4BC] to-[#F5CAC3] text-white font-medium hover:opacity-90 inline-flex items-center gap-1 shadow-sm disabled:opacity-60"
                data-testid="share-dashboard-btn"
                data-share-exclude="true"
                title="Capture your dashboard as an image to share"
              >
                {sharingDashboard ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                {sharingDashboard ? 'Capturing…' : 'Share my dashboard'}
              </button>
            </div>
          </motion.section>

        {/* WhatsApp sticky connect banner */}
        {whatsappStatus && !whatsappStatus.verified && !bannerDismissed && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl border border-emerald-200/80 bg-emerald-50/80 backdrop-blur px-4 py-3 flex items-center justify-between gap-3"
            data-testid="whatsapp-connect-banner"
            data-share-exclude="true"
          >
            <div className="flex items-start gap-3 flex-1">
              <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-4 h-4" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-emerald-900">Get a gentle play idea on WhatsApp.</p>
                <p className="text-emerald-700/80 text-xs">
                  Daily, weekly or monthly — your call. Pause anytime by replying PAUSE.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Link to="/parent-app/settings">
                <button
                  className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
                  data-testid="whatsapp-banner-connect-btn"
                >
                  Connect WhatsApp
                </button>
              </Link>
              <button
                onClick={() => setBannerDismissed(true)}
                className="p-1 rounded-full text-emerald-700/60 hover:text-emerald-900"
                data-testid="whatsapp-banner-dismiss-btn"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Monthly profile refresh */}
        {child?.id && (
          <div data-share-exclude="true">
            <ProfileRefreshCard childId={child.id} onSubmitted={() => loadChildBundle(child)} />
          </div>
        )}

        {/* ============ TODAY'S PLAY IDEA — centrepiece ============ */}
        {act && (
          <motion.section
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="relative overflow-hidden rounded-[2rem] bg-white/90 backdrop-blur-md border border-white shadow-[0_20px_60px_rgba(124,111,156,0.18)]"
            data-testid="today-play-idea"
          >
            {/* vintage gradient ribbon — Pastel Blue → Light Lavender → Soft Pink */}
            <div className="h-2 bg-gradient-to-r from-[#A3C4BC] via-[#EAD7D1] to-[#F5CAC3] " />

            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
                <div>
                  <div className="inline-flex items-center gap-2 text-[#7C6F9C] mb-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FFF8E1] to-[#FFE3B3] flex items-center justify-center">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.18em] font-semibold">Today's play idea</p>
                  </div>
                  <h2 className="font-serif text-2xl sm:text-3xl text-[#3a2f4f] leading-tight">
                    {act.activity_name}
                  </h2>
                  <p className="text-sm text-stone-500 mt-1.5">
                    {act.best_routine} · {act.time_needed}
                  </p>
                </div>
                <button
                  onClick={regeneratePlayIdea}
                  disabled={regenerating}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#7C6F9C]/8 hover:bg-[#7C6F9C]/15 text-[#7C6F9C] transition"
                  data-testid="regenerate-play-idea-btn"
                  data-share-exclude="true"
                >
                  {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  {regenerating ? 'Refreshing…' : 'New idea'}
                </button>
              </div>

              {/* Steps — numbered infographic-style blocks. Each step
                  shows a bold title, warm description, and a "why it
                  matters" language-reason line. Legacy activities that
                  only have `what_to_do` are coerced server-side into a
                  single step so this renders reliably. */}
              {act.steps?.length > 0 && (
                <div className="mb-5 space-y-3" data-testid="play-idea-steps">
                  {act.steps.map((step, i) => (
                    <div
                      key={i}
                      className="relative rounded-2xl bg-gradient-to-br from-[#FFF8E1] via-white to-[#F5CAC3]/40 border border-[#EAD7D1] p-4 pl-14"
                      data-testid={`play-idea-step-${i + 1}`}
                    >
                      <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-gradient-to-br from-[#A3C4BC] to-[#7C6F9C] text-white font-serif text-sm flex items-center justify-center shadow-sm">
                        {i + 1}
                      </div>
                      <p className="font-serif text-base sm:text-lg text-[#3a2f4f] leading-snug mb-1">
                        {step.title}
                      </p>
                      {step.description && (
                        <p className="text-sm text-stone-700 leading-relaxed mb-2">
                          {step.description}
                        </p>
                      )}
                      {step.why_it_matters && (
                        <p className="text-xs italic text-[#5A7A72] leading-relaxed">
                          💡 {step.why_it_matters}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Legacy fallback — old play ideas persisted before the
                  step-based rewrite may still have a flat `what_to_do`. */}
              {(!act.steps || act.steps.length === 0) && act.what_to_do && (
                <Section label="What to do">{act.what_to_do}</Section>
              )}
              {act.phrases_to_model?.length > 0 && (
                <Section label="Try modelling">
                  <ul className="flex flex-wrap gap-1.5">
                    {act.phrases_to_model.map((p, i) => (
                      <li key={i} className="px-3 py-1.5 bg-gradient-to-r from-[#FFF8E1] to-[#EAD7D1] rounded-xl text-[#3a2f4f] text-sm border border-[#EAD7D1]">
                        "{p}"
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
              <Section label="Accept any communication">{act.communication_to_accept}</Section>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Section label="Make it easier" small>{act.make_it_easier}</Section>
                <Section label="Make it richer" small>{act.make_it_richer}</Section>
              </div>
              <Section label="What to notice">{act.what_to_notice}</Section>

              {Array.isArray(act.goal_support) && act.goal_support.length > 0 && (
                <div className="mt-5 p-4 rounded-2xl bg-[#F5FAFE] border border-[#DCEBF7]" data-testid="play-idea-goal-support">
                  <p className="text-sm font-semibold text-[#3a2f4f] mb-2">🎯 Supporting Your Child&apos;s Goals</p>
                  <div className="space-y-1.5 mb-2">
                    {act.goal_support.map((g) => (
                      <p key={`${g.source}-${g.title}`} className="text-sm text-stone-800 flex items-center gap-2 flex-wrap">
                        <GoalBadge source={g.source} /> {g.title}
                      </p>
                    ))}
                  </div>
                  <p className="text-sm text-stone-700 leading-relaxed">{act.goal_support[0].how}</p>
                </div>
              )}

              {act.strategy_to_try && (
                <div
                  className="mt-5 p-4 rounded-2xl bg-gradient-to-br from-[#FFF8E1] to-[#FFE3B3] border border-[#FFE3B3]"
                  data-testid="play-idea-strategy"
                >
                  <p className="text-[10px] uppercase tracking-widest text-[#9C7430] font-semibold mb-1">
                    Gentle strategy · try this today
                  </p>
                  <p className="text-sm text-[#5C4318] leading-relaxed">
                    {act.strategy_to_try}
                  </p>
                </div>
              )}

              <div className="mt-5 p-4 rounded-2xl bg-gradient-to-br from-[#FFF8E1] to-[#EAD7D1] border border-[#EAD7D1]">
                <p className="text-sm italic text-[#5b4f7a]">💛 {act.encouraging_reminder}</p>
              </div>

              {act.glp_note && (
                <div className="mt-3 p-3 rounded-2xl bg-[#D9EAD3]/70 border border-[#D9EAD3]" data-testid="play-idea-glp-note">
                  <p className="text-xs text-emerald-900 leading-relaxed">
                    <span className="font-semibold uppercase tracking-wider">GLP note · </span>
                    {act.glp_note}
                  </p>
                </div>
              )}

              <div className="mt-6 flex items-center justify-between flex-wrap gap-3" data-share-exclude="true">
                {existingFeedback ? (
                  <div className="text-xs text-stone-500 flex items-center gap-2 flex-wrap" data-testid="play-idea-feedback-summary">
                    {existingFeedback.completed && (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <Check className="w-3.5 h-3.5" /> Completed
                      </span>
                    )}
                    {existingFeedback.useful === true && (
                      <span className="inline-flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" /> Useful</span>
                    )}
                    {existingFeedback.useful === false && (
                      <span className="inline-flex items-center gap-1"><ThumbsDown className="w-3.5 h-3.5" /> Not useful</span>
                    )}
                    {existingFeedback.difficulty && (
                      <span>Difficulty: {DIFF_LABEL[existingFeedback.difficulty]}</span>
                    )}
                    {existingFeedback.engagement && (
                      <span>· Engagement: {ENG_LABEL[existingFeedback.engagement]}</span>
                    )}
                  </div>
                ) : <span />}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={handleSharePdf}
                    disabled={sharingPdf}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#7C6F9C]/30 text-[#7C6F9C] hover:bg-[#7C6F9C]/5 text-sm font-medium transition disabled:opacity-60"
                    data-testid="share-play-idea-pdf-btn"
                    title="Share as PDF — perfect for nannies, grandparents, or other caregivers"
                  >
                    {sharingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                    {sharingPdf ? 'Preparing…' : 'Share PDF'}
                  </button>
                  <button
                    onClick={() => setShowFeedback(true)}
                    className="bg-gradient-to-r from-[#A3C4BC] to-[#7C6F9C] hover:from-[#92B5AC] hover:to-[#6B5E8A] text-white rounded-full px-5 py-2.5 font-medium shadow-md shadow-[#A3C4BC]/40 transition-all inline-flex items-center gap-2 text-sm"
                    data-testid="open-feedback-dialog-btn"
                  >
                    <MessageSquare className="w-4 h-4" />
                    {existingFeedback ? 'Update feedback' : 'Log how it went'}
                  </button>
                </div>
              </div>

              <Link
                to={`/parent-app/toys?child=${child.id}`}
                className="block mt-5 rounded-2xl bg-gradient-to-r from-[#FFE3B3]/60 via-[#FFF8E1] to-[#F5CAC3]/50 border border-[#EAD7D1] px-4 py-3 text-sm text-[#5b4f7a] hover:from-[#FFE3B3]/80 hover:to-[#F5CAC3]/70 transition group"
                data-testid="daily-idea-my-toys-hint"
              >
                <span className="font-medium">Want an idea for what they&apos;re into right now?</span>{' '}
                Snap a quick photo — a favourite toy, a running tap, a spinning wheel. Find it under{' '}
                <span className="underline group-hover:no-underline">Toys &amp; interests</span>.
              </Link>
            </div>
          </motion.section>
        )}

        {/* ============ QUICK ACCESS TILE GRID ============ */}
        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="quick-tiles-grid"
        >
          <h2 className="font-serif text-xl text-[#3a2f4f] mb-3 ml-1">Explore</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {QUICK_TILES.map((t) => {
              const Icon = t.icon;
              return (
                <Link
                  key={t.key}
                  to={t.href(child.id)}
                  className={`group relative overflow-hidden rounded-3xl bg-gradient-to-br ${t.bg} p-4 sm:p-5 min-h-[150px] flex flex-col justify-between border border-white/60 shadow-[0_8px_24px_rgba(124,111,156,0.08)] hover:shadow-[0_14px_40px_rgba(124,111,156,0.18)] hover:-translate-y-0.5 transition-all`}
                  data-testid={t.testid}
                >
                  <div className={`w-10 h-10 rounded-2xl ${t.iconBg} flex items-center justify-center backdrop-blur-sm`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#3a2f4f] leading-snug">{t.label}</h3>
                    <p className="text-xs text-[#3a2f4f]/70 mt-0.5 leading-snug">{t.desc}</p>
                  </div>
                  <ArrowRight className="absolute top-4 right-4 w-4 h-4 text-[#3a2f4f]/40 group-hover:text-[#3a2f4f] group-hover:translate-x-0.5 transition" />
                </Link>
              );
            })}
          </div>
        </motion.section>

          {/* PlayFirst signature for the shared image */}
          <div className="flex items-center justify-center gap-1.5 pt-2 pb-1 opacity-70" data-share-include="true">
            <Heart className="w-3.5 h-3.5 text-[#7C6F9C] fill-[#7C6F9C]/30" />
            <span className="text-xs text-[#7C6F9C] font-serif italic tracking-wide">PlayFirst Parent · playfirstapp.com</span>
          </div>
        </div>
        {/* ============ /SHAREABLE DASHBOARD CARD ============ */}

        {/* My Child's Goals — parent + therapist goals */}
        <MyChildGoalsCard childId={child?.id} />

        {/* Language Map snapshot — compact, contextual */}
        {lm && (
          <motion.section
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="bg-white/85 backdrop-blur-md border border-white shadow-[0_8px_32px_rgba(124,111,156,0.08)] rounded-3xl p-6"
            data-testid="language-map-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-[#7C6F9C]">
                <BookOpen className="w-5 h-5" />
                <p className="text-xs uppercase tracking-wider font-medium">
                  {childFirstName}'s Language Map
                </p>
              </div>
              <Link
                to="/parent-app/language-map"
                className="inline-flex items-center gap-1 text-xs text-[#7C6F9C] hover:underline"
                data-testid="view-full-language-map-link"
              >
                See full map <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <h3 className="font-serif text-xl text-[#3a2f4f] mb-2">
              How {childFirstName} communicates now
            </h3>
            <p className="text-stone-700 leading-relaxed mb-4">{lm.current_communication_summary}</p>

            {lm.todays_focus && (
              <div className="p-3 rounded-2xl bg-gradient-to-br from-[#FFF8E1] to-[#EAD7D1] border border-[#EAD7D1] text-sm text-stone-700">
                <span className="text-xs uppercase tracking-wider text-[#5A7A72] font-medium block mb-0.5">
                  Today's focus
                </span>
                {lm.todays_focus}
              </div>
            )}
          </motion.section>
        )}

        {/* Bloom */}
        <ParentBloomCard childFirstName={childFirstName} />

        {/* Recent moments */}
        <RecentMoments items={feedbackList} />

        <footer className="text-center text-xs text-stone-500 pt-8 space-y-1">
          <div className="space-x-3">
            <Link to="/parent-app/contact" className="hover:underline" data-testid="home-contact-link">Contact &amp; Support</Link>
            <span aria-hidden>·</span>
            <Link to="/privacy" className="hover:underline">Privacy</Link>
            <span aria-hidden>·</span>
            <Link to="/terms" className="hover:underline">Terms</Link>
          </div>
          <p>PlayFirst Parent is not a diagnostic, screening or emergency tool.</p>
        </footer>
      </main>

      <ActivityFeedbackDialog
        open={showFeedback}
        onOpenChange={setShowFeedback}
        playIdea={playIdea}
        existingFeedback={existingFeedback}
        onSaved={() => child && refreshFeedback(child.id)}
      />

      <WhatsAppOnboardSplash
        open={showWhatsappSplash}
        onClose={(persist) => {
          setShowWhatsappSplash(false);
          if (persist) {
            try { localStorage.setItem('playfirst_whatsapp_intro_seen', '1'); } catch {}
          }
        }}
      />
    </div>
  );
}

function Section({ label, children, small }) {
  return (
    <div className={small ? 'mb-3' : 'mb-5'}>
      <p className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium mb-1.5">
        {label}
      </p>
      <div className={`text-stone-700 ${small ? 'text-sm' : ''} leading-relaxed`}>{children}</div>
    </div>
  );
}

function RecentMoments({ items }) {
  if (!items || items.length === 0) return null;
  const meaningful = items.filter(
    (f) => f.notes || (f.new_words || []).length > 0 || f.difficulty || f.engagement || f.completed,
  );
  if (meaningful.length === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="bg-white/85 backdrop-blur-md border border-white shadow-[0_8px_32px_rgba(124,111,156,0.08)] rounded-3xl p-6"
      data-testid="recent-moments-card"
    >
      <div className="flex items-center gap-2 text-[#7C6F9C] mb-4">
        <MessageSquare className="w-5 h-5" />
        <p className="text-xs uppercase tracking-wider font-medium">Recent moments</p>
      </div>
      <ul className="space-y-3">
        {meaningful.slice(0, 5).map((m) => (
          <li
            key={m.id || m.play_idea_id}
            className="p-3 rounded-2xl bg-white/70 border border-stone-100"
            data-testid={`recent-moment-${m.play_idea_id}`}
          >
            <div className="flex items-center gap-2 flex-wrap text-xs text-stone-500 mb-1">
              {m.completed && (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <Check className="w-3 h-3" /> Completed
                </span>
              )}
              <span className="text-stone-700 font-medium text-sm">{m.activity_name || 'Activity'}</span>
            </div>
            {(m.new_words || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {m.new_words.map((w) => (
                  <span
                    key={w}
                    className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100"
                  >
                    + {w}
                  </span>
                ))}
              </div>
            )}
            {m.notes && <p className="text-sm text-stone-700 mt-1">{m.notes}</p>}
            <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-stone-500">
              {m.difficulty && <span>{DIFF_LABEL[m.difficulty]}</span>}
              {m.engagement && <span>· {ENG_LABEL[m.engagement]}</span>}
            </div>
          </li>
        ))}
      </ul>
    </motion.section>
  );
}
